import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import Image from 'next/image';

import { Article, ENTITY_TYPE_ENUM } from '@repo/types';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/ui/tooltip';

import { AnalysisTooltipContent } from '@/components/article-analysis/analysis-container';

const EntityName = memo<{
  name: string;
}>(({ name }) => {
  const [isNameTooltipOpen, setIsNameTooltipOpen] = useState(false);
  const nameTooltipRef = useRef<HTMLParagraphElement>(null);

  const handleNameTooltipOpenChange = useCallback((open: boolean) => {
    if (nameTooltipRef.current) {
      const isClamped =
        nameTooltipRef.current.scrollHeight >
        nameTooltipRef.current.clientHeight;
      setIsNameTooltipOpen(open && isClamped);
    }
  }, []);

  return (
    <TooltipProvider>
      <Tooltip
        delayDuration={200}
        open={isNameTooltipOpen}
        onOpenChange={handleNameTooltipOpenChange}
      >
        <TooltipTrigger asChild>
          <span
            ref={nameTooltipRef}
            className="line-clamp-2 text-xs font-medium leading-[14px] text-neutral-2 @cred-md:text-base @cred-md:leading-[19px] @cred-lg:text-base @cred-lg:leading-[19px] @cred-xl:text-lg @cred-xl:leading-[21px]"
          >
            {name}
          </span>
        </TooltipTrigger>
        <AnalysisTooltipContent className="z-200 max-w-72 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.3)] @cred-md:max-w-80 @cred-lg:max-w-[330px] @cred-xl:max-w-[440px]">
          <p className="text-xs font-medium leading-[14px] text-neutral-2 @cred-md:text-base @cred-md:leading-[19px] @cred-lg:text-base @cred-lg:leading-[19px] @cred-xl:text-lg @cred-xl:leading-[21px]">
            {name}
          </p>
        </AnalysisTooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

EntityName.displayName = 'EntityName';

const RoleOrAffiliation = memo<{
  roleOrAffiliation: string | null | undefined;
}>(({ roleOrAffiliation }) => {
  const [isRoleOrAfTooltipOpen, setIsRoleOrAfTooltipOpen] = useState(false);
  const roleOrAfTooltipRef = useRef<HTMLParagraphElement>(null);

  const handleRoleOrAfTooltipOpenChange = useCallback((open: boolean) => {
    if (roleOrAfTooltipRef.current) {
      const isClamped =
        roleOrAfTooltipRef.current.scrollHeight >
        roleOrAfTooltipRef.current.clientHeight;
      setIsRoleOrAfTooltipOpen(open && isClamped);
    }
  }, []);

  return (
    <TooltipProvider>
      <Tooltip
        delayDuration={200}
        open={isRoleOrAfTooltipOpen}
        onOpenChange={handleRoleOrAfTooltipOpenChange}
      >
        <TooltipTrigger asChild>
          <p
            ref={roleOrAfTooltipRef}
            className="line-clamp-3 text-[10px] font-medium leading-3 text-[#757575] @cred-md:text-sm @cred-md:leading-[15.2px] @cred-lg:text-sm @cred-lg:leading-[15.2px] @cred-xl:text-base @cred-xl:leading-[19px]"
          >
            {roleOrAffiliation}
          </p>
        </TooltipTrigger>
        <AnalysisTooltipContent className="z-200 max-w-72 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.3)] @cred-md:max-w-80 @cred-lg:max-w-[330px] @cred-xl:max-w-[440px]">
          <p className="text-xs leading-[13px] text-[#292929] @cred-md:text-[15px] @cred-md:leading-[17.5px] @cred-lg:text-base @cred-lg:leading-[19px] @cred-xl:text-xl @cred-xl:leading-[25px]">
            {roleOrAffiliation}
          </p>
        </AnalysisTooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

RoleOrAffiliation.displayName = 'RoleOrAffiliation';

const entityIconsMap = new Map<ENTITY_TYPE_ENUM, string>([
  [ENTITY_TYPE_ENUM.PERSON, '/images/icons/person.png'],
  [ENTITY_TYPE_ENUM.ORGANIZATION, '/images/icons/building.png'],
  [ENTITY_TYPE_ENUM.MEDIA, '/images/icons/paper.png'],
]);

interface KeySourcesProps {
  articleMetrics?: Article;
}

export const PureKeySources: React.FC<KeySourcesProps> = ({
  articleMetrics,
}) => {
  const entities = useMemo(() => {
    return articleMetrics?.entities?.sort((a, b) => {
      const typePriority = {
        [ENTITY_TYPE_ENUM.PERSON]: 1,
        [ENTITY_TYPE_ENUM.ORGANIZATION]: 2,
        [ENTITY_TYPE_ENUM.MEDIA]: 3,
      };

      const aPriority = typePriority[a.type as keyof typeof typePriority] ?? 4;
      const bPriority = typePriority[b.type as keyof typeof typePriority] ?? 4;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      return (b.sourceCredibility ?? 0) - (a.sourceCredibility ?? 0);
    });
  }, [articleMetrics?.entities]);

  return (
    <div className="flex flex-col gap-2 @cred-md:gap-[9.33px] @cred-lg:gap-[10.13px] @cred-xl:gap-3">
      {!entities ? (
        <div className="flex items-center gap-3 text-sm @cred-md:text-base @cred-md:leading-[19px] @cred-lg:text-base @cred-lg:leading-[19px] @cred-xl:text-lg @cred-xl:leading-[21px]">
          <span>No key sources found</span>
        </div>
      ) : entities.length === 0 ? (
        <div className="flex items-center gap-3 text-sm @cred-md:text-base @cred-md:leading-[19px] @cred-lg:text-base @cred-lg:leading-[19px] @cred-xl:text-lg @cred-xl:leading-[21px]">
          <span>No key sources found</span>
        </div>
      ) : (
        entities.map((entity) => {
          const icon = entityIconsMap.get(entity.type);

          return (
            <div
              key={entity.id}
              className="relative flex items-center gap-2 rounded-r-md bg-white py-1.5 pl-[18px] pr-2 transition-[background-color,transform,translate] duration-300 hover:-translate-y-0.5 hover:bg-white"
            >
              <div className="absolute inset-y-0 left-0 h-full w-2.5 bg-facts-pie-chart" />
              <div className="flex flex-1 flex-col gap-1">
                <EntityName name={entity.name} />
                <RoleOrAffiliation
                  roleOrAffiliation={entity.roleOrAffiliation}
                />
              </div>
              <div className="flex shrink-0 items-center gap-2 pr-2">
                {icon && (
                  <div className="relative flex size-3.5 shrink-0 @cred-md:size-5 @cred-lg:size-5 @cred-xl:size-6">
                    <Image
                      src={icon}
                      alt={entity.name}
                      width={100}
                      height={100}
                      className="object-contain"
                    />
                  </div>
                )}
                {/* {entity.sourceCredibility !== void 0 &&
                  entity.sourceCredibility !== null && (
                    <Badge className="flex h-fit shrink-0 whitespace-nowrap rounded-sm border-none bg-facts-pie-chart px-px py-0.5 text-white hover:bg-facts-pie-chart/80 @cred-md:px-[7px] @cred-md:py-[2.33px] @cred-lg:px-[7.6px] @cred-lg:py-[2.53px] @cred-xl:rounded-[10px] @cred-xl:px-2.5 @cred-xl:py-[3.33px]">
                      <span className="w-9 text-center text-sm font-medium leading-4 @cred-md:text-sm @cred-md:leading-4 @cred-lg:text-sm @cred-lg:leading-4 @cred-xl:text-lg @cred-xl:leading-[21px]">
                        {Number.isFinite(entity.sourceCredibility)
                          ? Math.round(entity.sourceCredibility ?? 0)
                          : 0}
                        %
                      </span>
                    </Badge>
                  )} */}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};
